import { supabase } from '@/lib/supabase'
import type {
  CrmCardScan,
  CrmContactCardScan,
  CrmContactDetail,
  CrmContactListItem,
  CrmContactListOut,
  CrmContactUpdate,
  CrmLabel,
  CrmList,
  CrmListMailchimpStatus,
} from './types'

const BASE = '/api/v1/crm'

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export function listContacts(params: {
  q?: string
  label_id?: string
  owner_user_id?: string
  category_id?: string
  limit?: number
  offset?: number
}): Promise<CrmContactListOut> {
  const query = new URLSearchParams()
  if (params.q) query.set('q', params.q)
  if (params.label_id) query.set('label_id', params.label_id)
  if (params.owner_user_id) query.set('owner_user_id', params.owner_user_id)
  if (params.category_id) query.set('category_id', params.category_id)
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset) query.set('offset', String(params.offset))
  const qs = query.toString()
  return request(`/contacts${qs ? `?${qs}` : ''}`)
}

export function getContact(personId: string): Promise<CrmContactDetail | { moved_to: string }> {
  return request(`/contacts/${personId}`)
}

export function createContact(input: {
  full_name: string
  email?: string
  phone?: string
  /** spec-63: an existing organisation wins over a name (found or created). */
  organisation_id?: string
  organisation_name?: string
  role?: string
  category_id?: string
  owner_user_id?: string
}): Promise<CrmContactDetail> {
  return request('/contacts', { method: 'POST', body: JSON.stringify(input) })
}

export function updateContact(
  personId: string,
  patch: CrmContactUpdate,
): Promise<CrmContactDetail> {
  return request(`/contacts/${personId}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export function setContactLabels(personId: string, labelIds: string[]): Promise<CrmContactDetail> {
  return request(`/contacts/${personId}/labels`, {
    method: 'PUT',
    body: JSON.stringify({ label_ids: labelIds }),
  })
}

export function setConsent(
  personId: string,
  input: { channel: string; status: string; source?: string },
): Promise<CrmContactDetail> {
  return request(`/contacts/${personId}/consents`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function listLabels(): Promise<CrmLabel[]> {
  return request('/labels')
}

export function createLabel(input: { name: string; kind?: string }): Promise<CrmLabel> {
  return request('/labels', { method: 'POST', body: JSON.stringify(input) })
}

export function listLists(): Promise<CrmList[]> {
  return request('/lists')
}

export function createList(input: {
  name: string
  type: string
  filter_json?: Record<string, unknown>
}): Promise<CrmList> {
  return request('/lists', { method: 'POST', body: JSON.stringify(input) })
}

export function updateList(
  listId: string,
  payload: {
    name?: string
    filter_json?: Record<string, unknown> | null
    mailchimp_sync_enabled?: boolean
    mailchimp_tag_name?: string | null
  },
): Promise<CrmList> {
  return request(`/lists/${listId}`, { method: 'PATCH', body: JSON.stringify(payload) })
}

export function deleteList(listId: string): Promise<void> {
  return request(`/lists/${listId}`, { method: 'DELETE' })
}

export function getListMailchimpStatus(listId: string): Promise<CrmListMailchimpStatus> {
  return request(`/lists/${listId}/mailchimp-status`)
}

export function setListMembers(listId: string, personIds: string[]): Promise<CrmList> {
  return request(`/lists/${listId}/members`, {
    method: 'PUT',
    body: JSON.stringify({ person_ids: personIds }),
  })
}

export function listListContacts(listId: string): Promise<CrmContactListItem[]> {
  return request(`/lists/${listId}/contacts`)
}

export function listExportUrl(listId: string): string {
  return `${BASE}/lists/${listId}/export`
}

export async function downloadXlsx(url: string, filename: string): Promise<void> {
  const response = await fetch(url, { headers: await authHeaders() })
  if (!response.ok) throw new Error(`Export mislukt (${response.status})`)
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

// ---------------------------------------------------------------------------
// Visitekaartjes-scans (spec-63 E5: scans hang aan lijsten; events zijn lijsten van de soort Event)
// ---------------------------------------------------------------------------

export function listListScans(listId: string): Promise<CrmCardScan[]> {
  return request(`/lists/${listId}/scans`)
}

export function retryScan(scanId: string): Promise<CrmCardScan> {
  return request(`/scans/${scanId}/retry`, { method: 'POST' })
}

/** Short-lived signed URL for the stored business-card photo. */
export function getScanPhotoUrl(scanId: string): Promise<{ url: string }> {
  return request(`/scans/${scanId}/photo`)
}

export function getContactCardScans(personId: string): Promise<CrmContactCardScan[]> {
  return request(`/contacts/${personId}/card-scans`)
}

/**
 * Upload one business-card photo to a static list. Sends `multipart/form-data`: the
 * browser sets the boundary, so we drop the JSON `Content-Type` and keep only
 * auth/accept (same pattern as preinvest/documents `uploadDocument`). A 422 and a 409
 * carry a Dutch message in `{error}` (type/size, archived list), which we surface verbatim.
 */
export async function uploadListScan(listId: string, file: File): Promise<CrmCardScan> {
  const headers = await authHeaders()
  delete headers['Content-Type']

  const body = new FormData()
  body.append('file', file)

  const response = await fetch(`${BASE}/lists/${listId}/scans`, {
    method: 'POST',
    headers,
    body,
  })
  if (!response.ok) {
    if (response.status === 422 || response.status === 409) {
      const data = (await response.json().catch(() => null)) as { error?: string } | null
      if (data?.error) throw new Error(data.error)
    }
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as CrmCardScan
}
